import { useEffect, useState, useMemo } from 'react';
import { Loader2, ChevronDown, ChevronUp, X, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Checkbox } from '../components/ui/checkbox';
import { Button } from '../components/ui/button';
import { PackageCard } from '../components/PackageCard';
import { getPackages } from '../lib/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GROUP_LABELS = {
  Cultural: { label: 'Cultural', color: 'text-purple-300', testid: 'group-cultural' },
  Sport: { label: 'Sport', color: 'text-emerald-300', testid: 'group-sport' },
  Altele: { label: 'Altele', color: 'text-amber-300', testid: 'group-altele' },
};

function parsePrice(price) {
  if (typeof price === 'number') return price;
  if (typeof price === 'string') {
    const m = price.replace(/\./g, '').match(/[\d]+/);
    return m ? parseInt(m[0]) : 0;
  }
  return 0;
}

export default function EvenimentePage() {
  const [packages, setPackages] = useState([]);
  const [categories, setCategories] = useState({ Cultural: [], Sport: [], Altele: [] });
  const [selectedSubcategories, setSelectedSubcategories] = useState([]);  // ["Cabaret","Teatru"]
  const [selectedGroups, setSelectedGroups] = useState([]);  // groups with no subcat selected = include all packages of that group
  const [expandedGroups, setExpandedGroups] = useState({ Cultural: true, Sport: true, Altele: true });
  const [sortOrder, setSortOrder] = useState('none');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [pkgs, catsRes] = await Promise.all([
          getPackages({ category: 'Concerte', event_type: 'eveniment' }),
          fetch(`${API_URL}/api/event-categories`).then(r => r.ok ? r.json() : { Cultural: [], Sport: [], Altele: [] }),
        ]);
        setPackages(pkgs || []);
        setCategories(catsRes || { Cultural: [], Sport: [], Altele: [] });
      } catch (e) {
        console.error('Evenimente load error:', e);
      } finally {
        setLoading(false);
      }
    };
    load();
    window.scrollTo(0, 0);
  }, []);

  const toggleGroup = (g) => setExpandedGroups(prev => ({ ...prev, [g]: !prev[g] }));

  const toggleSubcat = (group, name) => {
    const key = `${group}::${name}`;
    setSelectedSubcategories(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const toggleGroupAll = (group) => {
    setSelectedGroups(prev => prev.includes(group) ? prev.filter(g => g !== group) : [...prev, group]);
  };

  const filteredPackages = useMemo(() => {
    let result = [...packages];
    const hasFilters = selectedSubcategories.length > 0 || selectedGroups.length > 0;
    if (hasFilters) {
      result = result.filter(p => {
        // subcategory match
        if (selectedSubcategories.includes(`${p.event_group}::${p.event_subcategory}`)) return true;
        // group-level match (any subcategory in group)
        if (selectedGroups.includes(p.event_group)) return true;
        return false;
      });
    }
    if (sortOrder === 'asc') result.sort((a, b) => parsePrice(a.price) - parsePrice(b.price));
    else if (sortOrder === 'desc') result.sort((a, b) => parsePrice(b.price) - parsePrice(a.price));
    return result;
  }, [packages, selectedSubcategories, selectedGroups, sortOrder]);

  const handleReset = () => {
    setSelectedSubcategories([]);
    setSelectedGroups([]);
    setSortOrder('none');
  };

  const hasActive = selectedSubcategories.length > 0 || selectedGroups.length > 0 || sortOrder !== 'none';

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="evenimente-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/concerte" className="inline-flex items-center gap-1 text-gold text-sm mb-3 hover:underline" data-testid="back-to-hub">
          <ArrowLeft className="w-4 h-4" /> Înapoi la Concerte & Evenimente
        </Link>
        <div className="mb-8">
          <h1 className="font-heading font-bold text-3xl md:text-4xl text-white mb-2" data-testid="page-title">
            Evenimente
          </h1>
          <p className="text-white/60">Descoperă spectacole culturale, evenimente sportive și alte experiențe</p>
        </div>

        <div className="flex flex-col md:flex-row gap-8">
          {/* Sidebar */}
          <aside className="md:w-72 flex-shrink-0">
            <div className="sticky top-24 glass rounded-xl p-5 space-y-4" data-testid="evenimente-filters">
              {/* Sort */}
              <div className="border-b border-white/10 pb-4">
                <h4 className="font-heading font-semibold text-white mb-3">Sortare preț</h4>
                <div className="space-y-2">
                  <label className="flex items-center gap-3 cursor-pointer text-sm text-white/80">
                    <Checkbox checked={sortOrder === 'asc'} onCheckedChange={() => setSortOrder(sortOrder === 'asc' ? 'none' : 'asc')}
                      className="border-white/30 data-[state=checked]:bg-gold data-[state=checked]:border-gold" />
                    Preț crescător
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer text-sm text-white/80">
                    <Checkbox checked={sortOrder === 'desc'} onCheckedChange={() => setSortOrder(sortOrder === 'desc' ? 'none' : 'desc')}
                      className="border-white/30 data-[state=checked]:bg-gold data-[state=checked]:border-gold" />
                    Preț descrescător
                  </label>
                </div>
              </div>

              {/* Group accordions */}
              {Object.keys(GROUP_LABELS).map(group => {
                const meta = GROUP_LABELS[group];
                const items = categories[group] || [];
                const groupAllChecked = selectedGroups.includes(group);
                return (
                  <div key={group} className="border-b border-white/10 pb-4 last:border-b-0" data-testid={`accordion-${meta.testid}`}>
                    <button onClick={() => toggleGroup(group)} className="flex items-center justify-between w-full mb-2">
                      <h4 className={`font-heading font-semibold ${meta.color}`}>{meta.label}</h4>
                      {expandedGroups[group] ? <ChevronUp className="w-4 h-4 text-white/60" /> : <ChevronDown className="w-4 h-4 text-white/60" />}
                    </button>
                    {expandedGroups[group] && (
                      <div className="space-y-2 pl-1">
                        <label className="flex items-center gap-3 cursor-pointer text-sm text-white/70 italic">
                          <Checkbox checked={groupAllChecked} onCheckedChange={() => toggleGroupAll(group)}
                            className="border-white/30 data-[state=checked]:bg-gold data-[state=checked]:border-gold"
                            data-testid={`filter-${group.toLowerCase()}-all`} />
                          Toate ({meta.label})
                        </label>
                        {items.length === 0 ? (
                          <p className="text-xs text-white/40 italic px-1">Nicio subcategorie</p>
                        ) : (
                          items.map(it => {
                            const key = `${group}::${it.name}`;
                            return (
                              <label key={it.id} className="flex items-center gap-3 cursor-pointer text-sm text-white/80 hover:text-white"
                                data-testid={`filter-${group.toLowerCase()}-${it.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                                <Checkbox checked={selectedSubcategories.includes(key)} onCheckedChange={() => toggleSubcat(group, it.name)}
                                  className="border-white/30 data-[state=checked]:bg-gold data-[state=checked]:border-gold" />
                                {it.name}
                              </label>
                            );
                          })
                        )}
                      </div>
                    )}
                  </div>
                );
              })}

              {hasActive && (
                <Button onClick={handleReset} variant="outline" className="w-full border-gold/50 text-gold hover:bg-gold/10" data-testid="reset-filters">
                  <X className="w-4 h-4 mr-2" />
                  Resetează filtrele
                </Button>
              )}
            </div>
          </aside>

          {/* Main */}
          <main className="flex-1">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 text-gold animate-spin" />
              </div>
            ) : filteredPackages.length === 0 ? (
              <div className="text-center py-20 glass rounded-2xl" data-testid="no-events">
                <p className="text-white/60 text-lg mb-2">Niciun eveniment găsit</p>
                <p className="text-white/40 text-sm">Încearcă să schimbi filtrele sau revino mai târziu</p>
              </div>
            ) : (
              <>
                <div className="mb-4 text-white/60 text-sm">
                  {filteredPackages.length} {filteredPackages.length === 1 ? 'eveniment găsit' : 'evenimente găsite'}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="evenimente-grid">
                  {filteredPackages.map((pkg, i) => (
                    <div key={pkg.id} className="animate-fade-in" style={{ animationDelay: `${i * 50}ms` }}>
                      <PackageCard package={pkg} />
                    </div>
                  ))}
                </div>
              </>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
